import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  TextField,
  Button,
  IconButton,
  Skeleton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
  Chip,
  FormControlLabel,
  Switch,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import Page from 'src/components/Page';
import palette from 'src/theme/palette';
import { getFeaturesList, deleteFeaturesList, toggleBestFeature } from 'src/ApiServices';
import { toast } from 'react-toastify';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddEditFeaturesListModal from 'src/sections/dashboard-sections/FeaturesSections/AddEditFeaturesListModal';

export default function FeaturesList() {
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  });
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [bestModalOpen, setBestModalOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [pendingBestFeature, setPendingBestFeature] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bestLoading, setBestLoading] = useState(false);

  // Fetch features data function
  const fetchFeaturesData = async (currentPage, search = '') => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 10
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      const response = await getFeaturesList(params);
      setFeatures(response.data || []);

      if (response.pagination) {
        setPagination(response.pagination);
      } else {
        setPagination({
          current_page: currentPage,
          per_page: 10,
          total: response.data?.length || 0,
          last_page: Math.ceil((response.data?.length || 0) / 10) || 1
        });
      }
    } catch (error) {
      console.error('Failed to load features', error);
      toast.error('Failed to load features');
      setFeatures([]);
    } finally {
      setLoading(false);
    }
  };

  // Search handlers
  const handleSearchSubmit = useCallback(async () => {
    setPage(1);
    await fetchFeaturesData(1, searchQuery);
  }, [searchQuery]);

  const handleClearSearch = useCallback(async () => {
    setSearchQuery('');
    setPage(1);
    await fetchFeaturesData(1, '');
  }, []);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleAddClick = () => {
    setSelectedFeature(null);
    setModalOpen(true);
  };

  const handleEditClick = (feature) => {
    setSelectedFeature(feature);
    setModalOpen(true);
  };

  const handleDeleteClick = (feature) => {
    setSelectedFeature(feature);
    setDeleteModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setDeleteModalOpen(false);
    setBestModalOpen(false);
    setSelectedFeature(null);
    setPendingBestFeature(null);
  };

  const handleModalSuccess = () => {
    handleCloseModal();
    fetchFeaturesData(page, searchQuery);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedFeature) return;

    try {
      setDeleteLoading(true);
      await deleteFeaturesList(selectedFeature.id);
      toast.success('Feature deleted successfully!');

      if (features.length === 1 && page > 1) {
        const newPage = page - 1;
        setPage(newPage);
        await fetchFeaturesData(newPage, searchQuery);
      } else {
        await fetchFeaturesData(page, searchQuery);
      }

      setDeleteModalOpen(false);
      setSelectedFeature(null);
    } catch (error) {
      console.error('Failed to delete feature:', error);
      toast.error(error.response?.data?.message || 'Failed to delete feature!');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Best Toggle Logic (Max 5)
  const handleBestToggleChange = (feature) => (event) => {
    const checked = event.target.checked;

    if (checked && bestFeaturesCount >= 5) {
      setPendingBestFeature(feature);
      setBestModalOpen(true);
    } else {
      toggleBestStatus(feature, checked);
    }
  };

  const toggleBestStatus = async (feature, makeBest) => {
    const previousFeatures = [...features];
    const newStatus = makeBest ? 1 : 0;

    //  UI update
    setFeatures((prev) =>
      prev.map((f) => ({
        ...f,
        is_best: f.id === feature.id ? newStatus : makeBest && f.is_best === 1 ? 0 : f.is_best
      }))
    );

    try {
      setBestLoading(true);

      const res = await toggleBestFeature(feature.id, newStatus);

      // API SUCCESS MESSAGE
      toast.success(
        res?.message || (makeBest ? 'Marked as Best Feature!' : 'Removed from Best Features')
      );

      fetchFeaturesData(page, searchQuery);
    } catch (error) {
      setFeatures(previousFeatures);

      const apiMsg = error.response?.data?.message;
      const status = error.response?.status;

      // Status based toast
      if (status === 400) toast.error(apiMsg || 'Invalid request!');
      else if (status === 429) toast.error('Too many requests – wait a moment!');
      else if (status === 500) toast.error('Server error – try again!');
      else toast.error(apiMsg || 'Failed to update best status!');
    } finally {
      setBestLoading(false);
    }
  };

  const confirmBestToggle = () => {
    if (pendingBestFeature) {
      toggleBestStatus(pendingBestFeature, true);
    }
    setBestModalOpen(false);
    setPendingBestFeature(null);
  };

  const bestFeaturesCount = features.filter((f) => f.is_best === 1 || f.is_best === true).length;

  const fetchCategoriesData = async () => {
    const mockCategories = [
      { id: 1, name: 'UI Features' },
      { id: 2, name: 'API Features' },
      { id: 3, name: 'Security Features' }
    ];
    setCategories(mockCategories);
  };

  // Load features when page changes
  useEffect(() => {
    fetchFeaturesData(page, searchQuery);
  }, [page]);

  // Load initial data
  useEffect(() => {
    fetchFeaturesData(1, '');
    fetchCategoriesData();
  }, []);

  const getStatusColor = (status) => (status === 'active' ? 'success' : 'error');

  const stickyNameColumnStyle = {
    px: 2,
    position: { xs: 'static', md: 'sticky' },
    left: 0,
    zIndex: 1,
    backgroundColor: theme.palette.background.paper,
    minWidth: 200
  };
  const stickyActionsColumnStyle = {
    px: 2,
    position: { xs: 'static', md: 'sticky' },
    right: 0,
    width: 140,
    textAlign: 'center',
    zIndex: 1,
    backgroundColor: theme.palette.background.paper
  };

  const renderSkeletonRows = () => {
    return Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={index}>
        <TableCell sx={stickyNameColumnStyle}>
          <Skeleton variant="text" />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" width={120} />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" width={80} />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="rectangular" width={60} height={32} />
        </TableCell>
        <TableCell sx={stickyActionsColumnStyle}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="circular" width={32} height={32} />
          </Box>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Page title={`Features List | ${process.env.REACT_APP_PROJECTNAME}`}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            mt: 2,
            pb: 2,
            pl: 1
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, md: 25 },
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.titledark
            }}
          >
            Features List
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{
              background: palette.primary.main,
              color: palette.primary.contrastText,
              height: 40,
              '&:hover': { background: palette.primary.dark }
            }}
          >
            Add Feature
          </Button>
        </Box>

        {/* Search Section */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={10}>
            <TextField
              variant="outlined"
              placeholder="Search features..."
              size="medium"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: palette.text.secondary }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch} edge="end">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                backgroundColor: theme.palette.background.paper,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: theme.palette.divider },
                  '&:hover fieldset': { borderColor: theme.palette.primary.main },
                  '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearchSubmit}
                fullWidth
                sx={{
                  height: 56,
                  fontSize: '14px',
                  fontFamily: 'poppinsMedium'
                }}
                startIcon={<SearchIcon />}
              >
                Search
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer
              sx={{
                width: '100%',
                overflowX: { xs: 'auto', md: 'hidden' },
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': { height: 6 },
                '&::-webkit-scrollbar-track': { background: palette.grey[200] },
                '&::-webkit-scrollbar-thumb': { background: palette.grey[400], borderRadius: 4 }
              }}
            >
              <Table
                sx={{
                  minWidth: { xs: '600px', md: '100%' },
                  '& .MuiTableCell-head': {
                    backgroundColor: theme.palette.background.paper,
                    color: palette.primary.tableHeadTitle,
                    fontFamily: 'poppinsSemiBold',
                    fontSize: '14px',
                    borderBottom: `2px solid ${palette.divider}`,
                    py: 2
                  },
                  '& .MuiTableCell-body': {
                    color: palette.text.primary,
                    fontFamily: 'poppinsRegular',
                    fontSize: '14px',
                    borderBottom: `1px solid ${palette.divider}`,
                    py: 2
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...stickyNameColumnStyle, zIndex: 2 }}>Feature Name</TableCell>
                    <TableCell sx={{ px: 2 }}>Category</TableCell>
                    <TableCell sx={{ px: 2 }}>Sub Category</TableCell>
                    <TableCell sx={{ px: 2 }}>Status</TableCell>
                    <TableCell sx={{ px: 2, textAlign: 'center' }}>Homepage Quiz</TableCell>
                    <TableCell sx={{ ...stickyActionsColumnStyle, zIndex: 2 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    renderSkeletonRows()
                  ) : features.length > 0 ? (
                    features.map((feature) => (
                      <TableRow
                        key={feature.id}
                        sx={{
                          '&:hover': { backgroundColor: palette.action.hover },
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        <TableCell sx={stickyNameColumnStyle}>{feature.name || 'N/A'}</TableCell>
                        <TableCell sx={{ px: 2 }}>{feature?.category_name || 'N/A'}</TableCell>
                        <TableCell sx={{ px: 2 }}>{feature?.sub_category_name || 'N/A'}</TableCell>
                        <TableCell sx={{ px: 2 }}>
                          <Chip
                            label={feature.status || 'active'}
                            color={getStatusColor(feature.status)}
                            size="small"
                          />
                        </TableCell>

                        <TableCell sx={{ px: 2, textAlign: 'center' }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={
                                  feature.is_best === 1 ||
                                  feature.is_best === true ||
                                  feature.is_best === '1'
                                }
                                onChange={handleBestToggleChange(feature)}
                                color="primary"
                                size="small"
                                disabled={bestLoading}
                              />
                            }
                            label=""
                          />
                        </TableCell>
                        <TableCell sx={stickyActionsColumnStyle}>
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(feature)}
                                sx={{ color: palette.primary.main }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(feature)}
                                sx={{ color: palette.error.main }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">
                          {searchQuery
                            ? 'No features found matching your search'
                            : 'No features found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Pagination */}
        {!loading && pagination.total >= 0 && pagination.last_page >= 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
            <Pagination
              count={pagination.last_page}
              page={pagination.current_page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        {/* Modals */}
        <AddEditFeaturesListModal
          open={modalOpen}
          onClose={handleCloseModal}
          onSuccess={handleModalSuccess}
          feature={selectedFeature}
          categories={categories}
        />

        {/* Delete Confirmation Modal */}
        <Dialog
          open={deleteModalOpen}
          onClose={handleCloseModal}
          PaperProps={{
            sx: {
              borderRadius: 2,
              padding: 1,
              margin: { xs: 2, sm: 3 },
              maxWidth: '400px',
              width: '100%'
            }
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', py: 3, pb: 2 }}>
            <WarningAmberIcon
              sx={{
                fontSize: 60,
                color: palette.warning.main,
                mb: 2
              }}
            />
            <Typography
              variant="h6"
              sx={{ fontFamily: 'poppinsSemiBold', color: palette.text.primary }}
            >
              Confirm Deletion
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
            <DialogContentText sx={{ color: palette.text.secondary, fontFamily: 'poppinsRegular' }}>
              Are you sure you want to delete feature <strong>"{selectedFeature?.name}"</strong>?
              <br />
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3 }}>
            <Button
              onClick={handleCloseModal}
              variant="outlined"
              sx={{
                borderColor: palette.grey[400],
                color: palette.text.primary,
                minWidth: 100
              }}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              sx={{
                backgroundColor: palette.error.main,
                color: palette.error.contrastText,
                minWidth: 100,
                '&:hover': {
                  backgroundColor: palette.error.dark
                }
              }}
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Best Feature Confirmation Modal */}
        <Dialog open={bestModalOpen} onClose={handleCloseModal}>
          <DialogTitle>Limit Reached</DialogTitle>
          <DialogContent>
            <DialogContentText>
              You can only mark <strong>5 features</strong> as Best. Marking "
              <strong>{pendingBestFeature?.name}</strong>" as Best will remove the oldest one.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button onClick={confirmBestToggle} variant="contained" disabled={bestLoading}>
              {bestLoading ? 'Updating...' : 'Mark as Best'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Page>
  );
}
